import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import HeaderDropdownListItem from "./HeaderDropdownListItem";
import HeaderSocialLink from "./HeaderSocialLink";
import { getDecodedHtml } from "utils/getDecodedHtml";
import kebabCase from "utils/kebabCase";
import { headerGA4Props } from "./types/public-header-types";
import { ImageArrow } from "components/utility/images";

const HeaderListItem = ({
	children,
	className = "",
	dataset,
	index,
	isLastSubNav,
	mobileNav,
	showSubNav,
	onToggle,
	theme
}) => {
	const {
		url,
		label,
		navItems = [],
		linkClasses = "",
		showSocialLinks,
		isExternaLink,
		socialLinks,
		ariaLabel,
		rel,
		title,
		dataTargetTitle
	} = dataset;

	const [isActive, setIsActive] = useState(" "); // initial state must not be empty string
	const toggleActiveMain = (status) => setIsActive(status);

	const toggleActive = useCallback((url) => {
		const currentLinkArray = url.split("/");
		const currentLinkItem = currentLinkArray[currentLinkArray.length - 1];
		const currentPage = window.location.pathname.split("/");

		if (currentPage !== undefined) {
			currentPage.forEach((currentPageItem) => {
				if (currentPageItem !== "" && currentLinkItem === currentPageItem) {
					setIsActive(" active ");
				} else {
					setIsActive("");
				}
			});
		} else {
			setIsActive("");
		}
	}, []);

	useEffect(() => {
		toggleActive(url);
	}, [toggleActive, url]);

	const setTabMobileClass = () => {
		if (showSubNav === index) {
			return " selected show ";
		} else if (showSubNav === -1) {
			return "";
		} else {
			return " hide ";
		}
	};

	const createDropdownList = (navItems, showSocialLinks, socialLinks) => {
		let dropdownList = [];
		navItems.forEach((item) => {
			if (item.type === "group" && item.navItems) {
				dropdownList.push(<HeaderDropdownListItem data={item} key={item.label} />);
				item.navItems.map((subItem, index, array) =>
					dropdownList.push(
						<HeaderDropdownListItem
							isGroupedItem={true}
							isFirstInGroup={index === 0}
							isLastInGroup={index === array.length - 1 ? true : false}
							toggleActiveMain={toggleActiveMain}
							data={subItem}
							parent={item.label}
							key={subItem.label}
							qaAttr={`nav-header-link-${kebabCase(subItem.label)}`}
						/>
					)
				);
			}
			if (item.type === "group" && !item.navItems) {
				dropdownList.push(<HeaderDropdownListItem data={item} key={item.label} />);
			}

			if (item.type === "link") {
				dropdownList.push(
					<HeaderDropdownListItem
						toggleActiveMain={toggleActiveMain}
						data={item}
						key={item.label}
						qaAttr={`nav-header-link-${kebabCase(item.label)}`}
					/>
				);
			}
		});

		return (
			<ul className={`nav-links-sub ${isLastSubNav ? "nav-links-sub-last" : ""}`} role="menu">
				{dropdownList.map((item) => item)}
				{showSocialLinks && !mobileNav ? (
					<li className="follow-us-nav">
						<HeaderSocialLink data={socialLinks} theme={theme} />
					</li>
				) : null}
			</ul>
		);
	};

	return navItems.length > 0 ? (
		<li
			className={
				"nav-links-main-label " +
				className +
				(navItems.length > 0 ? " nav-links-main-list " : "") +
				(showSubNav === index && !mobileNav ? " selected " : "") +
				(label === "Audience Switcher" ? " nav-links-main-audience" : "") +
				(mobileNav ? setTabMobileClass() : "") +
				(isLastSubNav ? " nav-links-main-list-last " : "") +
				isActive
			}
			role="none"
			onClick={onToggle}
			onKeyDown={onToggle}
		>
			<a
				aria-label={ariaLabel || getDecodedHtml(label)}
				aria-expanded={showSubNav === index && !mobileNav}
				className={linkClasses}
				href={url || "/"}
				data-a="true"
				data-a-lbl={getDecodedHtml(label)}
				data-a-cat={"Navigation"}
				data-a-act={"Header"}
				data-a-evt={headerGA4Props["data-a-evt"]}
				data-a-loc={headerGA4Props["data-a-loc"]}
				data-a-text={getDecodedHtml(label)}
				data-a-comp={headerGA4Props["data-a-comp"]}
				data-target-title={dataTargetTitle}
				data-qa={`nav-header-link-${getDecodedHtml(kebabCase(label))}`}
				role="menuitem"
			>
				{children ? (
					children
				) : (
					<div
						className="nav-links-main-title"
						dangerouslySetInnerHTML={{ __html: getDecodedHtml(label) }}
					/>
				)}
				{<ImageArrow />}
			</a>
			{navItems.length ? createDropdownList(navItems, showSocialLinks, socialLinks) : null}
		</li>
	) : (
		<li
			className={`nav-links-main-label ${className} ${
				mobileNav ? setTabMobileClass() : ""
			} ${isActive}`}
			role="none"
		>
			<a
				href={url || "/"}
				target={isExternaLink ? "_blank" : "_self"}
				title={title}
				className={linkClasses}
				aria-label={ariaLabel || label}
				data-a="true"
				data-a-lbl={getDecodedHtml(label)}
				data-a-cat={"Navigation"}
				data-a-act={"Header"}
				data-a-evt={headerGA4Props["data-a-evt"]}
				data-a-loc={headerGA4Props["data-a-loc"]}
				data-a-text={getDecodedHtml(label)}
				data-a-comp={headerGA4Props["data-a-comp"]}
				data-target-title={dataTargetTitle}
				data-qa={`nav-header-link-${getDecodedHtml(kebabCase(label))}`}
				rel={rel || "noreferrer"}
				role="menuitem"
			>
				<div
					className="nav-links-main-title"
					dangerouslySetInnerHTML={{ __html: getDecodedHtml(label) }}
				/>
			</a>
		</li>
	);
};

HeaderListItem.propTypes = {
	dataset: PropTypes.shape({
		url: PropTypes.string,
		label: PropTypes.string,
		fullNameLabel: PropTypes.string,
		showSocialLinks: PropTypes.bool,
		isExternaLink: PropTypes.bool,
		navItems: PropTypes.array
	}),
	children: PropTypes.oneOfType([
		PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.node, PropTypes.element])),
		PropTypes.node,
		PropTypes.element
	]),
	className: PropTypes.string,
	index: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	isLastSubNav: PropTypes.bool,
	mobileNav: PropTypes.bool,
	showSubNav: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
	onToggle: PropTypes.func
};

export default HeaderListItem;
